<script setup lang="ts">
  import { FeedbackService } from '@/types/generated/services.gen'
  import {
    InformationCircleIcon,
    XMarkIcon,
    HandThumbUpIcon,
    HandThumbDownIcon
  } from '@heroicons/vue/24/outline'
  import { ref, onMounted, onUnmounted, nextTick } from 'vue'
  import { FeatureEnum } from '@/types/generated'

  const props = defineProps({
    explanation: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    }
  })

  const isPopupOpen = ref(false)
  const rating = ref<'positive' | 'negative' | null>(null)
  const popupRef = ref<HTMLDivElement | null>(null)
  const buttonRef = ref<HTMLButtonElement | null>(null)
  const showAbove = ref(false)
  const scrollTick = ref(0)
  const scrollX = ref(0)
  const scrollY = ref(0)
  const popupLeft = ref(0)
  const popupTop = ref(0)
  const INFO_POPUP_OPEN_EVENT = 'jupus-info-open'
  // Spacing/gutter to keep popup detached from trigger and viewport edges
  const POPUP_MARGIN = 16
  // Prefer rendering above when the trigger enters the lower 40% of viewport
  const VERTICAL_PLACEMENT_THRESHOLD = 0.6
  const instanceId = crypto.randomUUID()
  const eventController = new AbortController()
  const handleGlobalOpen = (evt: Event) => {
    const customEvent = evt as CustomEvent<{ instanceId: string }>
    if (customEvent.detail?.instanceId !== instanceId && isPopupOpen.value) {
      closePopup()
    }
  }

  const determinePosition = () => {
    if (!buttonRef.value || !isPopupOpen.value) return

    const buttonRect = buttonRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    // Use a small fixed gutter to keep the popup visually detached from the
    // trigger and viewport edges; 16px matches common UI spacing scales.

    // Decide vertical placement
    // If the trigger sits in the lower 40% of the viewport, prefer rendering
    // above to reduce the chance of the popup overflowing below the fold.
    // The 0.6 threshold provides a slight bias to render below when there's
    // still enough space, improving perceived stability around the middle.
    showAbove.value = buttonRect.top > viewportHeight * VERTICAL_PLACEMENT_THRESHOLD

    const popupWidth = popupRef.value?.offsetWidth || 0
    const popupHeight = popupRef.value?.offsetHeight || 0

    // Compute desired top
    let desiredTop = showAbove.value
      ? buttonRect.top + window.scrollY - POPUP_MARGIN - popupHeight
      : buttonRect.bottom + window.scrollY + POPUP_MARGIN

    // Do not clamp vertically; let it follow the icon even off-screen
    popupTop.value = desiredTop

    // Compute desired left to the right of the button
    let desiredLeft = buttonRect.right + window.scrollX

    // If it would overflow right, try placing to the left of the button
    const maxLeft = window.scrollX + viewportWidth - popupWidth - POPUP_MARGIN
    if (desiredLeft > maxLeft) {
      const leftOfButton = buttonRect.left + window.scrollX - popupWidth
      desiredLeft = Math.max(window.scrollX + POPUP_MARGIN, leftOfButton)
    }

    // Final clamp within viewport
    const minLeft = window.scrollX + POPUP_MARGIN
    popupLeft.value = Math.min(Math.max(desiredLeft, minLeft), Math.max(minLeft, maxLeft))
  }

  const handleInfoClick = (event: MouseEvent) => {
    event.stopPropagation()
    isPopupOpen.value = true
    // Inform other instances to close
    window.dispatchEvent(new CustomEvent(INFO_POPUP_OPEN_EVENT, { detail: { instanceId } }))
    nextTick(() => {
      updateOffsetsAndPosition({ bumpTick: true })
    })
  }

  const closePopup = () => {
    isPopupOpen.value = false
  }

  const rate = async (rating: 'positive' | 'negative') => {
    await FeedbackService.feedbackCreate({
      requestBody: {
        rating: rating === 'positive' ? '5.0' : '0.0',
        feature: FeatureEnum.AI_ANALYSIS,
        url: window.location.href,
        related_data: {
          ai_analysis: { ai_augmentation_uuid: props.id }
        }
      }
    })
  }

  const handleRating = (value: 'positive' | 'negative') => {
    rating.value = value
    rate(value)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      isPopupOpen.value &&
      popupRef.value &&
      buttonRef.value &&
      !popupRef.value.contains(event.target as Node) &&
      !buttonRef.value.contains(event.target as Node)
    ) {
      closePopup()
    }
  }

  const updateOffsetsAndPosition = (options?: { bumpTick?: boolean }) => {
    scrollX.value = window.scrollX
    scrollY.value = window.scrollY
    if (options?.bumpTick) {
      // Force a re-compute of the popup style based on current scroll
      scrollTick.value++
    }
    determinePosition()
  }

  const handleScroll = () => {
    if (isPopupOpen.value) {
      updateOffsetsAndPosition({ bumpTick: true })
    }
  }

  const handleResize = () => {
    if (isPopupOpen.value) {
      updateOffsetsAndPosition()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside, { signal: eventController.signal })
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      signal: eventController.signal
    })
    window.addEventListener('resize', handleResize, { signal: eventController.signal })
    window.addEventListener(INFO_POPUP_OPEN_EVENT, handleGlobalOpen, {
      signal: eventController.signal
    })
    // Initialize scroll offsets
    updateOffsetsAndPosition()
  })

  onUnmounted(() => {
    eventController.abort()
  })
</script>

<template>
  <div class="relative inline-flex">
    <button
      ref="buttonRef"
      type="button"
      class="ms-2 inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white
        shadow-sm hover:bg-gray-50"
      @click="handleInfoClick"
    >
      <InformationCircleIcon class="h-5 w-5 text-gray-500" />
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          v-if="isPopupOpen"
          ref="popupRef"
          :style="{
            position: 'absolute',
            left: popupLeft + 'px',
            top: popupTop + 'px',
            // Include a CSS variable to create a reactive dependency on scrollTick without visual effect
            '--tick': String(scrollTick)
          }"
          class="z-50 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
        >
          <!-- Header with close button -->
          <div class="flex items-start justify-between">
            <p class="text-sm text-gray-600">
              {{ explanation }}
            </p>
            <button
              type="button"
              class="ml-4 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-500"
              @click="closePopup"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <!-- Rating Section -->
          <div class="mt-4 border-t border-gray-100 pt-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-500">War diese Erklärung hilfreich?</span>
              <div class="flex gap-2">
                <button
                  :class="[
                    'rounded-md p-1.5 transition-colors',
                    rating === 'positive'
                      ? 'bg-purple-100 text-purple-600'
                      : 'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
                  ]"
                  @click="handleRating('positive')"
                >
                  <HandThumbUpIcon class="h-5 w-5" />
                </button>
                <button
                  :class="[
                    'rounded-md p-1.5 transition-colors',
                    rating === 'negative'
                      ? 'bg-purple-100 text-purple-600'
                      : 'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
                  ]"
                  @click="handleRating('negative')"
                >
                  <HandThumbDownIcon class="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
